import React, { useState } from "react";
import { getFromLocalStorage, saveToLocalStorage } from "./EncryptDecrypt.jsx";
import { domain } from "./Domain.jsx";

// Shared refresh token control
let isRefreshing = false;
let refreshPromise = null;

const useApiRequest = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 🔄 Function to refresh access token (with lock)
    const fetchRefreshToken = async () => {
        if (isRefreshing) {
            return refreshPromise;
        }

        isRefreshing = true;
        refreshPromise = (async () => {
            const token = await getFromLocalStorage("authInfo");

            if (!token) throw new Error("No refresh token available");

            const response = await fetch(`${domain}/accounts/token/refresh`, {
                method: "POST",
                body: JSON.stringify({ refresh: token.refreshToken }),
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) throw new Error("Failed to refresh token");

            const data = await response.json();

            saveToLocalStorage("authInfo", {
                ...token,
                accessToken: data.access,
                refreshToken: data.refresh,
            });

            return data.access;
        })()
            .finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });

        return refreshPromise;
    };

    // 🔄 General API Request Handler
    const sendRequest = async (url, method, data = null, isMultipart = false) => {
        setLoading(true);
        setError(null);

        try {
            let headers = {};
            let body = null;
            let token = await getFromLocalStorage("authInfo");

            if (token) {
                headers.Authorization = `Token ${token.accessToken}`;
            }

            if (data !== null) {
                if (isMultipart) {
                    body = data;
                } else {
                    headers["Content-Type"] = "application/json";
                    body = JSON.stringify(data);
                }
            }

            let requestOptions = { method, headers, body };
            let apiResponse = await fetch(url, requestOptions);
            let responseData = null;

            // Handle no-content status codes early
            if (apiResponse.status === 204) return { message: "Delete was successful", status: 204 };
            if (apiResponse.status === 205) return { message: "Logout successful", status: 205 };

            // 🔁 Refresh token on 401
            if (apiResponse.status === 401) {
                try {
                    console.log("🔄 Refreshing access token...");
                    const newAccessToken = await fetchRefreshToken();

                    // Retry the request with the new access token
                    headers.Authorization = `Token ${newAccessToken}`;
                    requestOptions.headers = headers;
                    apiResponse = await fetch(url, requestOptions);
                } catch (refreshError) {
                    console.error("🚨 Token refresh failed, logging out user...");
                    localStorage.removeItem("authInfo")
                    localStorage.removeItem("isLoggedIn")
                    return {
                        success: false,
                        message: "Unauthorized. Please log in again.",
                        status: 401,
                    };
                }
            }

            // Try to parse the response (if applicable)
            const contentType = apiResponse.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                try {
                    responseData = await apiResponse.json();
                } catch (parseError) {
                    console.error("Failed to parse JSON response:", parseError);
                    responseData = null;
                }
            }

            // Return response with consistent structure for both success and error cases
            if (!apiResponse.ok) {
                return {
                    success: false,
                    status: apiResponse.status,
                    body: responseData,
                    message: responseData?.message || responseData?.error || `Request failed with status ${apiResponse.status}`,
                    details: responseData,
                };
            }

            return { 
                success: true,
                status: apiResponse.status, 
                body: responseData 
            };
        } catch (err) {
            console.error("🚨 API Request Error:", err);

            const errorObj = {
                message: err?.message || "Something went wrong",
                status: err?.status || null,
                details: err?.details || null,
            };

            setError(errorObj);
            return { success: false, ...errorObj };
        } finally {
            setLoading(false);
        }
    };

    // 🔹 API Methods
    const postRequest = async (url, data, isMultipart = false) => await sendRequest(url, "POST", data, isMultipart);
    const putRequest = async (url, data, isMultipart = false) => await sendRequest(url, "PUT", data, isMultipart);
    const deleteRequest = async (url) => await sendRequest(url, "DELETE");
    const getRequest = async (url) => await sendRequest(url, "GET");

    return { postRequest, putRequest, deleteRequest, getRequest, loading, error };
};

export default useApiRequest;



/*
import React, { useState } from 'react';
//import { useAuthContext } from './AuthContext.jsx';
import { getFromLocalStorage } from './EncryptDecrypt.jsx';

const useApiRequest = () => {
    //const { authToken } = useAuthContext();
    const [loading, setLoading] = useState(false)

    const sendRequest = async (url, method, data = null) => {
        try {
            let headers = {};

            //if (authToken !== null) {
                const token = await getFromLocalStorage("authToken");
                if(token){
                    headers.Authorization = `Token ${token.token}`;
                }
            //}
            //console.log("there is no token!")

            const requestOptions = {
                method: method,
                headers: headers,
                body: data !== null ? data : null,
            };

            const apiResponse = await fetch(url, requestOptions);
            const responseData = await apiResponse.json();
            console.log(responseData)
            setLoading(false)
            return responseData

        } catch (err) {
            return err
        }
    };

    const postRequest = async (url, data) => {
        return await sendRequest(url, 'POST', data);
    };
    
    const putRequest = async (url, data) => {
        return await sendRequest(url, 'PUT', data);
    };
    
    const deleteRequest = async (url) => {
        return await sendRequest(url, 'DELETE');
    };
    
    const getRequest = async (url) => {
        return await sendRequest(url, 'GET');
    };

    return { postRequest, putRequest, deleteRequest, getRequest, loading, setLoading };
};

export default useApiRequest;
*/
